import {HttpErrorResponse} from '@angular/common/http';
import {computed, DestroyRef, inject, Injectable, Signal, signal} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {Observable} from 'rxjs';
import {CommunicationService} from '../../http/communication.service';
import {AuthorizationService} from '../authorization.service';
import {PolarisStateService} from './polaris-state.service';
import {PolarisV2Service} from '../polaris-v2/polaris-v2.service';
import {
  GetChatsOverviewRequest,
  GetChatsOverviewResponse,
  SendMessageRequest,
  UpdateChatTitleRequest
} from '../polaris-v2/polaris-v2.models';
import {
  POLARIS_FAILURE_MESSAGE,
  PolarisAccountKey,
  PolarisChatAggregate,
  PolarisChatId,
  PolarisChatVm,
  PolarisFacadeContract,
  PolarisFacadeViewState,
  PolarisMessageVm,
  PolarisModelVersion,
  PolarisOpenChatResultVm,
  PolarisPaginationParams,
  PolarisSendMessageOutcomeVm,
  PolarisSendMessageOptionsVm,
  PolarisStartChatOptionsVm,
  PolarisWizardVm
} from './model';
import {FIVE_MINUTES_IN_MS} from '../../components/copilot/copilot.model';

@Injectable({
  providedIn: 'root'
})
export class PolarisFacadeService implements PolarisFacadeContract {
  private polarisStateService = inject(PolarisStateService);
  private polarisV2Service = inject(PolarisV2Service);
  private communicationService = inject(CommunicationService);
  private authorizationService = inject(AuthorizationService);
  private destroyRef = inject(DestroyRef);

  public readonly myBrand = signal<string | null>(null);
  public readonly myCompetitors = signal<string[]>([]);
  public readonly customerNameSignal = signal<string>('');
  public readonly state: PolarisFacadeViewState = {
    chats: computed(() => {
      const map: Record<string, PolarisChatVm> = {};
      for (const chat of this.polarisStateService.chatList()) {
        map[chat.overview.chatId] = this.toChatVm(chat);
      }
      return map;
    }),
    chatHistoryItems: computed(() => this.polarisStateService.chatList().map(chat => ({
      id: chat.overview.chatId,
      title: chat.overview.title,
      lastUpdate: this.toLastUpdate(chat),
      modelVersion: chat.overview.modelVersion,
      myBrand: chat.turns[chat.turns.length - 1]?.myBrand,
      myCompetitors: chat.turns[chat.turns.length - 1]?.myCompetitors
    }))),
    activeChat: computed(() => {
      const active = this.polarisStateService.activeChat();
      return active ? this.toChatVm(active) : null;
    }),
    activeChatMessages: computed(() => {
      const active = this.polarisStateService.activeChat();
      return active ? this.toMessageVms(active) : [];
    }),
    myBrand: this.myBrand,
    myCompetitors: this.myCompetitors,
    customerName: computed(() => this.customerNameSignal()),
    isLoadingOverview: computed(() => this.polarisStateService.loading().overview),
    isLoadingActiveChat: computed(() => this.polarisStateService.loading().activeChat),
    isSendingMessage: computed(() => this.polarisStateService.loading().sendMessage),
    canLoadMoreChats: computed(() => this.polarisStateService.canLoadMoreChats()),
    recentChatId: computed(() => this.polarisStateService.recentChatId()),
  };

  constructor() {
    this.authorizationService.userProfile$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(profile => {
        this.customerNameSignal.set(profile.firstName ?? '');
      });
  }

  public async init(accountKey: PolarisAccountKey): Promise<void> {
    this.polarisStateService.initialize(accountKey);
    await this.loadChatsOverview();
  }

  public getChat(chatId: PolarisChatId): PolarisChatVm | undefined {
    const chat = this.polarisStateService.chatList().find(item => item.overview.chatId === chatId);
    return chat ? this.toChatVm(chat) : undefined;
  }

  public getChatMessages(chatId: PolarisChatId): Signal<PolarisMessageVm[]> {
    return computed(() => {
      const state = this.state.chats();
      return state[chatId]?.messages ?? [];
    });
  }

  public async loadChatsOverview(pagination: PolarisPaginationParams = {}): Promise<void> {
    const accountKey = this.requireAccountKey();
    this.polarisStateService.setOverviewLoading(true);
    this.polarisStateService.setOverviewError(null);
    const chatOverviewRequest: GetChatsOverviewRequest = {
      accountKey,
      limit: pagination.limit,
      offset: pagination.offset
    }
    try {
      const response: GetChatsOverviewResponse = await this.polarisV2Service.getChatsOverview(chatOverviewRequest);
      this.polarisStateService.upsertOverviewItems(response.items);
      this.polarisStateService.setPagination({
        limit: response.limit,
        offset: response.offset,
        total: response.total
      });
    } catch {
      this.polarisStateService.setOverviewError('Failed to load chat history');
    } finally {
      this.polarisStateService.setOverviewLoading(false);
    }
  }

  public async openChat(chatId: PolarisChatId): Promise<PolarisOpenChatResultVm> {
    const existing = this.getChat(chatId);
    const isHydrated = this.polarisStateService.isChatHydrated(chatId);
    if (existing && isHydrated) {
      this.polarisStateService.setActiveChat(chatId);
      return {
        found: true,
        loadedFromServer: false
      };
    }

    const accountKey = this.requireAccountKey();
    this.polarisStateService.setActiveChatLoading(true);
    this.polarisStateService.setActiveChatError(null);
    try {
      const response = await this.polarisV2Service.getChatById({accountKey, chatId});
      this.polarisStateService.upsertChat(response.chat);
      this.polarisStateService.setActiveChat(chatId);
      return {
        found: true,
        loadedFromServer: true
      };
    } catch {
      this.polarisStateService.setActiveChatError('Chat was not found');
      return {
        found: false,
        loadedFromServer: false
      };
    } finally {
      this.polarisStateService.setActiveChatLoading(false);
    }
  }

  public async ensureChatLoaded(chatId: PolarisChatId): Promise<boolean> {
    const result = await this.openChat(chatId);
    return result.found;
  }

  public async startChat(prompt: string, options?: PolarisStartChatOptionsVm): Promise<PolarisChatId> {
    const accountKey = this.requireAccountKey();
    const userId = this.authorizationService.userProfile.id;
    const localChatId = crypto.randomUUID();
    const nowIso = new Date().toISOString();

    const optimisticChat: PolarisChatAggregate = {
      overview: {
        chatId: localChatId,
        accountKey,
        userId,
        title: this.generateChatTitle(prompt),
        parentChatId: null,
        modelVersion: options?.modelVersion,
        createdAt: nowIso,
        lastModified: nowIso,
        lastMessageTime: nowIso
      },
      turns: [],
      isThinking: false,
      isNewlyCreated: true
    };

    this.polarisStateService.upsertChat(optimisticChat);
    this.polarisStateService.setActiveChat(localChatId);
    // Send first message as chat-creation request (without chatId).
    void this.sendMessageInternal(localChatId, prompt, options, true);
    return localChatId;
  }

  public async sendMessage(
    chatId: PolarisChatId | null,
    prompt: string,
    options?: PolarisSendMessageOptionsVm
  ): Promise<PolarisSendMessageOutcomeVm> {
    return this.sendMessageInternal(chatId, prompt, options, false);
  }

  private async sendMessageInternal(
    chatId: PolarisChatId | null,
    prompt: string,
    options: PolarisSendMessageOptionsVm | undefined,
    sendAsCreateRequest: boolean
  ): Promise<PolarisSendMessageOutcomeVm> {
    if (!chatId) {
      return {status: 'failed'};
    }
    const accountKey = this.requireAccountKey();
    const requestId = crypto.randomUUID();
    const interactionId = crypto.randomUUID();
    const timestamp = Date.now();
    const widgetContext = options?.widgetContext || options?.origin
      ? {
        ...(options?.widgetContext ?? {}),
        origin: options?.widgetContext?.origin ?? options?.origin
      }
      : undefined;

    const {brandKey, competitorsKeys} = this.resolveSendBrandContext(chatId, sendAsCreateRequest);

    this.polarisStateService.setSendLoading(true);
    this.polarisStateService.setSendError(null);
    this.polarisStateService.appendOptimisticTurn({
      chatId,
      userPrompt: prompt,
      requestId,
      interactionId,
      timestamp,
      origin: widgetContext?.origin,
      widgetName: widgetContext?.name,
      dataDomain: options?.dataDomain ?? undefined,
      widgetContext,
      myBrand: brandKey,
      myCompetitors: competitorsKeys
    });

    const request: SendMessageRequest = {
      chatId,
      createNewChat: sendAsCreateRequest,
      accountKey,
      modelVersion: options?.modelVersion ?? this.getChatModelVersion(chatId),
      userPrompt: prompt,
      dataDomain: options?.dataDomain ?? undefined,
      brandKey,
      competitorsKeys,
      widgetContext,
      wizard: options?.wizard,
      alert: options?.alert,
      signal: options?.signal
    };

    try {
      const response = await this.polarisV2Service.sendMessage(request);
      let resolvedChatId = response.chat.chatId;

      const enrichedTurn = {
        ...response.turn,
        messagePair: {
          ...response.turn.messagePair,
          requestId,
          interactionId,
          userPrompt: prompt,
          userTimestamp: timestamp
        }
      };
      this.polarisStateService.reconcileSendSuccess({
        chat: response.chat,
        turn: enrichedTurn
      });
      console.log(`[POLARIS] Response received for chat ${chatId}`);
      return {status: 'ok', chatId: resolvedChatId};
    } catch (error) {
      console.log(`[POLARIS] Error received for chat ${chatId}`, error);
      const isChatNotFound = error instanceof HttpErrorResponse && error.status === 404;
      if (isChatNotFound) {
        this.polarisStateService.markTurnFailed(chatId, interactionId, 'Chat was not found');
        return {status: 'chat_not_found'};
      }
      this.polarisStateService.markTurnFailed(chatId, interactionId, 'Failed to generate response');
      return {status: 'failed'};
    } finally {
      this.polarisStateService.setSendLoading(false);
    }
  }

  public async loadMoreChats(): Promise<void> {
    if (!this.polarisStateService.canLoadMoreChats()) {
      return;
    }
    const chatCount = this.polarisStateService.chatList().length;
    await this.loadChatsOverview({
      limit: this.polarisStateService.pagination().limit,
      offset: chatCount
    });
  }

  public setUserBrandContext(brand: string | null, competitors: string[]): void {
    this.myBrand.set(brand);
    this.myCompetitors.set([...competitors]);
  }

  /**
   * Resolves which brands to attach to an outgoing message (DEV-497).
   *
   * `myBrand`/`myCompetitors` are a global, app-wide selection that any entry point
   * (home picker, ask-Polaris drawer, signals/alerts) can change, so it may hold the
   * brands of a different chat or tab. An existing chat's brand context is fixed at
   * creation, so we always reuse the brands already on the chat's turns instead of the
   * global selection. The global is only used when opening a brand-new chat.
   */
  private resolveSendBrandContext(
    chatId: PolarisChatId,
    sendAsCreateRequest: boolean
  ): {brandKey: string | undefined; competitorsKeys: string[]} {
    if (!sendAsCreateRequest) {
      const turns = this.polarisStateService.getChatById(chatId)?.turns ?? [];
      const latestTurn = turns[turns.length - 1];
      if (latestTurn) {
        return {
          brandKey: latestTurn.myBrand ?? undefined,
          competitorsKeys: latestTurn.myCompetitors ?? []
        };
      }
    }
    return {
      brandKey: this.myBrand() ?? undefined,
      competitorsKeys: this.myCompetitors()
    };
  }

  public getWizardsResponse(): Observable<PolarisWizardVm[]> {
    return this.communicationService.httpGet<PolarisWizardVm[]>('/polaris/v2/wizards-config', {
      params: {full: true}
    });
  }

  public async updateChatTitle(chatId: PolarisChatId, newTitle: string): Promise<void> {
    const accountKey = this.requireAccountKey();
    const titleBeforeChange = this.polarisStateService.getChatById(chatId)?.overview.title;

    this.polarisStateService.setChatTitle(chatId, newTitle);
    const chatTitleUpdateRequest: UpdateChatTitleRequest = {
      accountKey,
      chatId,
      title: newTitle
    }
    try {
      const confirmedOverview = await this.polarisV2Service.updateChatTitle(chatTitleUpdateRequest);
      const chat = this.polarisStateService.getChatById(chatId);
      if (chat) {
        this.polarisStateService.upsertChat({...chat, overview: confirmedOverview});
      }
    } catch {
      if (titleBeforeChange !== undefined) {
        this.polarisStateService.setChatTitle(chatId, titleBeforeChange);
      }
      throw new Error('Failed to update chat title');
    }
  }

  public setRecentChatId(chatId: PolarisChatId): void {
    this.polarisStateService.setRecentChatId(chatId);
    this.clearRecentChatIdTimeout();
    this.polarisStateService.setRecentChatIdTimeout(
      setTimeout(() => {
        this.resetRecentChatId();
      }, FIVE_MINUTES_IN_MS)
    );
  }

  public resetRecentChatId(): void {
    this.polarisStateService.setRecentChatId(null);
    this.clearRecentChatIdTimeout();
  }

  private clearRecentChatIdTimeout(): void {
    const timeout = this.polarisStateService.recentChatIdTimeout();
    if (timeout) {
      clearTimeout(timeout);
      this.polarisStateService.setRecentChatIdTimeout(null);
    }
  }

  private toChatVm(chat: PolarisChatAggregate): PolarisChatVm {
    const latestTurn = chat.turns[chat.turns.length - 1];
    return {
      id: chat.overview.chatId,
      title: chat.overview.title,
      lastUpdate: this.toLastUpdate(chat),
      messages: this.toMessageVms(chat),
      modelVersion: chat.overview.modelVersion,
      myBrand: latestTurn?.myBrand,
      myCompetitors: latestTurn?.myCompetitors,
      origin: latestTurn?.origin,
      widgetName: latestTurn?.widgetName,
      widgetContext: latestTurn?.widgetContext,
      isNewlyCreated: chat.isNewlyCreated
    };
  }

  private toMessageVms(chat: PolarisChatAggregate): PolarisMessageVm[] {
    return chat.turns.flatMap(turn => {
      const messages: PolarisMessageVm[] = [
        {
          id: `${turn.id}-user`,
          author: 'user',
          content: turn.messagePair.userPrompt,
          timestamp: turn.messagePair.userTimestamp,
          interactionId: turn.messagePair.interactionId,
          origin: turn.origin,
          widgetName: turn.widgetName,
          widgetContext: turn.widgetContext
        }
      ];

      if (turn.messagePair.response || turn.status === 'pending' || turn.status === 'failed') {
        messages.push({
          id: turn.messagePair.id,
          author: 'assistant',
          content: turn.status === 'failed' ? POLARIS_FAILURE_MESSAGE : turn.messagePair.response,
          timestamp: turn.messagePair.systemTimestamp ?? turn.messagePair.userTimestamp,
          graphs: turn.messagePair.graphs.map(graph => ({
            title: graph.title,
            type: graph.type,
            url: graph.url ?? undefined,
            x: graph.x,
            series: graph.series.map(series => ({
              name: series.name,
              y: series.y,
              unit: series.unit ?? undefined
            })),
            y_is_percentage: graph.yIsPercentage,
            x_axis: graph.xAxis,
            y_axis: graph.yAxis
          })),
          followUpQuestions: turn.messagePair.followUpQuestions,
          insights: turn.messagePair.insights?.join('\n'),
          citations: turn.messagePair.citations,
          webInsights: turn.messagePair.webInsights,
          chatLibrary: turn.messagePair.chatLibrary,
          creativesURLs: turn.messagePair.creativesURLs,
          error: turn.messagePair.error,
          interactionId: turn.messagePair.interactionId,
          origin: turn.origin,
          widgetName: turn.widgetName,
          widgetContext: turn.widgetContext
        });
      }

      return messages;
    });
  }

  private toLastUpdate(chat: PolarisChatAggregate): number {
    const value = chat.overview.lastModified ?? chat.overview.lastMessageTime ?? chat.overview.createdAt;
    return Date.parse(value) || Date.now();
  }

  private requireAccountKey(): PolarisAccountKey {
    const accountKey = this.polarisStateService.accountKey();
    if (!accountKey) {
      throw new Error('Polaris account key is not initialized');
    }
    return accountKey;
  }

  private getChatModelVersion(chatId: PolarisChatId): PolarisModelVersion | undefined {
    const chat = this.polarisStateService.chatList().find(item => item.overview.chatId === chatId);
    return chat?.overview.modelVersion;
  }

  private generateChatTitle(prompt: string, maxWords = 20): string {
    return prompt.trim().split(/\s+/).slice(0, maxWords).join(' ') || 'New Chat';
  }
}
